// 環境変数の型定義
export interface EnvironmentConfig {
  env: 'development' | 'production' | 'staging' | 'test';
  apiBaseUrl: string;
  frontendBaseUrl: string;
  aiAgentUrl: string;
  debug: {
    enableLogs: boolean;
    enableMockData: boolean;
    showDevTools: boolean;
    logLevel: 'debug' | 'info' | 'warn' | 'error';
  };
  api: {
    timeout: number;
    retryCount: number;
  };
}

/**
 * 環境に応じたデフォルトAPI URLを取得
 * 本番環境: Cloud Run サービス URL
 */
const getDefaultApiUrl = (env: string): string => {
  switch (env) {
    case 'development':
      return 'http://localhost:3001';
    case 'production':
      return 'https://talent-hub-backend-603361083582.asia-northeast1.run.app';
    case 'staging':
      return 'https://talentscan-backend-staging-603361083582.asia-northeast1.run.app';
    case 'test':
      return 'http://localhost:3001';
    default:
      return 'http://localhost:3001';
  }
};

/**
 * 環境に応じたデフォルトフロントエンド URLを取得
 * 面接URL生成に使用
 */
const getDefaultFrontendUrl = (env: string): string => {
  switch (env) {
    case 'development':
      return 'http://localhost:3000';
    case 'production':
      return 'https://service.talenthub-inc.com'; // 本番用ドメイン
    case 'staging':
      return 'https://talent-scan-staging.web.app';
    case 'test':
      return 'http://localhost:3000';
    default:
      return 'http://localhost:3000';
  }
};

/**
 * 環境に応じたデフォルトAI面接エージェント URLを取得
 */
const getDefaultAIAgentUrl = (env: string): string => {
  switch (env) {
    case 'development':
      return 'http://localhost:4111';
    case 'production':
      return 'https://interview-agent-603361083582.asia-northeast1.run.app';
    case 'staging':
      return 'https://interview-agent-staging-603361083582.asia-northeast1.run.app';
    case 'test':
      return 'http://localhost:4111';
    default:
      return 'http://localhost:4111';
  }
};

/**
 * 環境別設定を取得
 */
export const getEnvironmentConfig = (): EnvironmentConfig => {
  const env = process.env.NODE_ENV || 'development';
  const customEnv = process.env.REACT_APP_ENV;

  // REACT_APP_ENVが設定されていればそれを優先、なければNODE_ENVを使用
  const actualEnv = customEnv || env;

  const config: EnvironmentConfig = {
    env: actualEnv as 'development' | 'production' | 'staging' | 'test',
    apiBaseUrl: process.env.REACT_APP_API_BASE_URL || getDefaultApiUrl(actualEnv),
    frontendBaseUrl: process.env.REACT_APP_FRONTEND_BASE_URL || getDefaultFrontendUrl(actualEnv),
    aiAgentUrl: process.env.REACT_APP_AI_AGENT_URL || getDefaultAIAgentUrl(actualEnv),
    debug: {
      enableLogs:
        process.env.REACT_APP_ENABLE_CONSOLE_LOGS === 'true' || actualEnv === 'development',
      enableMockData: process.env.REACT_APP_ENABLE_MOCK_DATA === 'true' || false,
      showDevTools: process.env.REACT_APP_SHOW_DEV_TOOLS === 'true' || actualEnv === 'development',
      logLevel:
        (process.env.REACT_APP_LOG_LEVEL as 'debug' | 'info' | 'warn' | 'error') ||
        (actualEnv === 'development' ? 'debug' : 'warn'),
    },
    api: {
      timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '10000', 10),
      retryCount: parseInt(process.env.REACT_APP_API_RETRY_COUNT || '3', 10),
    },
  };

  // 開発環境でのデバッグ情報出力
  if (config.debug.enableLogs && actualEnv === 'development') {
    console.log('🚀 Environment Config Loaded:', {
      env: config.env,
      apiBaseUrl: config.apiBaseUrl,
      frontendBaseUrl: config.frontendBaseUrl,
      aiAgentUrl: config.aiAgentUrl,
      debugMode: config.debug,
    });
  }

  return config;
};

/**
 * 現在の環境設定インスタンス
 */
export const environmentConfig = getEnvironmentConfig();

/**
 * 環境判定ヘルパー関数
 */
export const isDevelopment = (): boolean => environmentConfig.env === 'development';
export const isProduction = (): boolean => environmentConfig.env === 'production';
export const isStaging = (): boolean => environmentConfig.env === 'staging';
export const isTest = (): boolean => environmentConfig.env === 'test';

/**
 * デバッグ用ログ関数
 */
export const debugLog = (message: string, data?: any): void => {
  if (environmentConfig.debug.enableLogs) {
    const logLevel = environmentConfig.debug.logLevel;
    switch (logLevel) {
      case 'debug':
        console.log(`🐛 [DEBUG] ${message}`, data || '');
        break;
      case 'info':
        console.info(`ℹ️ [INFO] ${message}`, data || '');
        break;
      case 'warn':
        console.warn(`⚠️ [WARN] ${message}`, data || '');
        break;
      case 'error':
        console.error(`❌ [ERROR] ${message}`, data || '');
        break;
    }
  }
};

/**
 * API設定を取得
 */
export const getApiConfig = () => ({
  baseURL: environmentConfig.apiBaseUrl,
  timeout: environmentConfig.api.timeout,
  retryCount: environmentConfig.api.retryCount,
});
